export interface PTTStateMachineOptions {
  swipeCancelThreshold: number;
  onAccept: () => void;
  onCancel: () => void;
}

export interface PTTStateSnapshot {
  isHolding: boolean;
  isCanceled: boolean;
  swipeProgress: number;
}

/**
 * PTT hold-swipe gesture state. DOM orchestration stays in the widget renderer.
 *
 *   startHold()            → isHolding=true, isCanceled=false, swipeProgress=0
 *   updateSwipe(distance)  → clamps progress to [0,1]; fires onCancel ONCE when
 *                            distance crosses swipeCancelThreshold during a hold
 *   endHold()              → clears hold; fires onAccept unless already canceled
 *   reset()                → clears state silently (post-animation cleanup)
 */
export class PTTStateMachine {
  private holding = false;
  private canceled = false;
  private progress = 0;
  private readonly swipeCancelThreshold: number;
  private readonly onAccept: () => void;
  private readonly onCancel: () => void;

  constructor(options: PTTStateMachineOptions) {
    this.swipeCancelThreshold = options.swipeCancelThreshold;
    this.onAccept = options.onAccept;
    this.onCancel = options.onCancel;
  }

  startHold(): void {
    this.holding = true;
    this.canceled = false;
    this.progress = 0;
  }

  updateSwipe(distance: number): void {
    if (!this.holding || this.canceled) return;
    const ratio = distance / this.swipeCancelThreshold;
    this.progress = ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
    if (distance >= this.swipeCancelThreshold) {
      this.canceled = true;
      this.onCancel();
    }
  }

  endHold(): void {
    if (!this.holding) return;
    const wasCanceled = this.canceled;
    this.holding = false;
    this.progress = 0;
    if (!wasCanceled) {
      this.onAccept();
    }
  }

  reset(): void {
    this.holding = false;
    this.canceled = false;
    this.progress = 0;
  }

  getState(): PTTStateSnapshot {
    return {
      isHolding: this.holding,
      isCanceled: this.canceled,
      swipeProgress: this.progress,
    };
  }
}
